// Learner session token (bearer auth).
//
// DIVERGENCE from the *nxu-ui family: applynxu-ui authenticates with an HS256 JWT
// carried in the `nxu_apply_auth` cookie (validated at APIM). assessnxu-ui does NOT.
// Per design §14.2, the LTI 1.3 launch terminates at the assessment Api, which issues
// a short-lived learner *session JWT* and 302-redirects to this app carrying that
// token. We store it and send it as `Authorization: Bearer <token>` on every call.
//
// Why not the cookie: the app renders inside a Canvas LTI iframe (cross-site), where a
// SameSite cookie would be dropped by the browser. A bearer token sidesteps that.
//
// QA-preview mode: reviewers launch with a QA token in the same slot, which the service
// treats as a preview-scoped session (draft editions, no passback).

const STORAGE_KEY = 'assessnxu.session.jwt';

// Names of the query-string params a QA preview link may use.
const LAUNCH_TOKEN_PARAMS = ['session_token', 'token'] as const;

/**
 * Capture the session token handed over by the LTI launch and persist it to
 * sessionStorage (cleared when the tab closes). The service deliberately puts the
 * token in `#session=...`, so it is never sent to a web server or access log. Query
 * parameters remain supported for QA preview links.
 */
export const captureLaunchToken = (
  search: string = window.location.search,
  hash: string = window.location.hash,
): string | null => {
  const fragment = new URLSearchParams(hash.replace(/^#/, ''));
  const fragmentToken = fragment.get('session');
  if (fragmentToken) {
    setSessionToken(fragmentToken);
    window.history.replaceState(null, '', `${window.location.pathname}${search}`);
    return fragmentToken;
  }

  const params = new URLSearchParams(search);
  for (const key of LAUNCH_TOKEN_PARAMS) {
    const value = params.get(key);
    if (value) {
      setSessionToken(value);
      return value;
    }
  }
  return getSessionToken();
};

/**
 * Can this browser hold a session at all?
 *
 * A read that returns null cannot tell "no session yet" from "this browser refuses storage to
 * embedded content" — and the two need opposite instructions: one says reopen from Canvas, the other
 * says get out of the iframe, because reopening lands in the same blocked frame. A write-probe is the
 * only way to tell them apart, and it has to be a real write: some browsers expose the object and
 * throw on use.
 */
export const isSessionStorageAvailable = (): boolean => {
  try {
    const probe = '__nxu_probe__';
    window.sessionStorage.setItem(probe, '1');
    window.sessionStorage.removeItem(probe);
    return true;
  } catch {
    return false;
  }
};

export const getSessionToken = (): string | null => {
  try {
    return window.sessionStorage.getItem(STORAGE_KEY);
  } catch {
    // sessionStorage can be unavailable in some iframe/partitioned-storage contexts.
    return null;
  }
};

export const setSessionToken = (token: string): void => {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, token);
  } catch {
    // no-op: token stays in-memory only for this navigation
  }
};

export const clearSessionToken = (): void => {
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // no-op
  }
};

/**
 * Bearer auth header for the current session token, or an empty object when there is
 * no token (public/health routes).
 */
export const authHeaders = (): Record<string, string> => {
  const token = getSessionToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};
